/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { call, put, SagaReturnType, takeLatest } from "redux-saga/effects";
import { formatError } from "utils/errorHelper";
import api from "_config/api";

export interface ObterDetalheTipoRequerimentoRequest {
  idTipoRequerimento: string;
}

// D2 — configuração do "Campo fixo de protocolo" definida pela customização.
// ⚠️ O backend ainda NÃO expõe isto no detalhe do tipo. Enquanto vier undefined,
// o campo não é renderizado.
export interface ProtocoloVinculadoConfig {
  habilitado: boolean;
  idTipoRequerimentoVinculado?: string | null;
  nomeTipoRequerimentoVinculado?: string | null;
  rotulo?: string | null;
  orientacao?: string | null;
  obrigatorio: boolean;
}

export interface DetalheTipoRequerimento {
  id: string;
  ordem?: number;
  nome: string;
  descricao: string;
  situacao: string;
  secoes: DetalheSecao[];
  protocoloVinculado?: ProtocoloVinculadoConfig | null;
}

export interface DetalheSecao {
  id: string;
  ordem?: number;
  nome: string;
  repetivel: boolean;
  // ⚠️ O host CIDADÃO retorna o tipo de seção como STRING (o Name do enum),
  // ex.: "endereco" (diferente do host colaborador, que envia { value, name }).
  tipoSecao?: string;
  questoes: DetalheQuestao[];
}

export interface DetalheQuestao {
  id: string;
  ordem?: number;
  enunciado: string;
  obrigatoria: boolean;
  tipoQuestao:
    | "short_text"
    | "large_text"
    | "numeric"
    | "date"
    | "select_list"
    | "multi_select"
    | "document"
    | "map";
  metadata?: Record<string, any> | null;
  opcoes: DetalheOpcao[];
}

export interface DetalheOpcao {
  id: string;
  value?: number;
  text: string;
  tipoOpcao: "text_list" | "item_list";
  opcoes: {
    id: string;
    value?: number;
    text: string;
  }[];
}

interface State {
  isRequesting: boolean;
  successPayload: DetalheTipoRequerimento | null;
  errors: string[];
}

const initialState: State = {
  isRequesting: false,
  successPayload: null,
  errors: []
};

const obterDetalheTipoRequerimentoSlice = createSlice({
  name: "licenciamentourbano@obterDetalheTipoRequerimento",
  initialState,
  reducers: {
    obterDetalheTipoRequerimento: (
      state,
      action: PayloadAction<ObterDetalheTipoRequerimentoRequest>
    ): void => {
      state.isRequesting = true;
      state.errors = [];
    },
    obterDetalheTipoRequerimentoSuccess: (
      state,
      action: PayloadAction<DetalheTipoRequerimento>
    ): void => {
      state.isRequesting = false;
      state.successPayload = action.payload;
    },
    obterDetalheTipoRequerimentoFailed: (
      state,
      action: PayloadAction<string[]>
    ): void => {
      state.isRequesting = false;
      state.errors = action.payload;
    }
  }
});

function* sagaFunction(params: {
  type: string;
  payload: ObterDetalheTipoRequerimentoRequest;
}) {
  try {
    const response: SagaReturnType<typeof api> = yield call(
      api.get,
      `api/v1/cidadao/licenciamentourbano/${params.payload.idTipoRequerimento}`
    );

    const { data } = response;

    yield put(obterDetalheTipoRequerimentoSuccess(data));
  } catch (error) {
    yield put(obterDetalheTipoRequerimentoFailed(formatError(error)));
  }
}

export const {
  obterDetalheTipoRequerimento,
  obterDetalheTipoRequerimentoSuccess,
  obterDetalheTipoRequerimentoFailed
} = obterDetalheTipoRequerimentoSlice.actions;

export const { reducer } = obterDetalheTipoRequerimentoSlice;

export const saga = takeLatest(obterDetalheTipoRequerimento.type, sagaFunction);
